/**
 * Role model + the Firestore-backed user directory.
 *
 * Roles are stored in Firestore (`users/{uid}`) so admins can reassign them at runtime via the
 * Admin Panel — no backend code. A small hardcoded BOOTSTRAP_ADMINS list guarantees there is
 * always an admin (can't be locked out, and works even before the directory has any data).
 *
 * A user can hold MULTIPLE roles; each role grants access to a set of pages (views). `admin` is a
 * superuser — it grants every page (plus the manage-users capability). A user with NO roles is
 * locked out (sees the "no access" screen until an admin assigns one).
 *
 * Extensible: to add a role, add it to `Role` + `ALL_ROLES` + `ROLE_LABELS` + `ROLE_META`, and either
 * list its pages in ROLE_VIEWS (a normal role) or handle it in `canViewWithRoles` (a superuser like
 * admin). ROLE_META is what the Admin Panel shows: the group a role sits under and one line on what
 * it unlocks.
 *
 * UI-gating only: like the login gate, roles hide/show UI but the backend does not yet enforce
 * them (Firestore rules DO enforce who may WRITE roles — see firestore.rules). One exception:
 * socialiq-bqv itself checks social_direct_internal (or admin) before it serves the Social Brand
 * internal report.
 */
import {
  collection, doc, getDoc, getDocs, serverTimestamp, setDoc,
} from "firebase/firestore";
import { db } from "./firebase";

export type Role =
  | "admin" | "creator_management" | "social_boosting" | "invoicing" | "partner_network"
  | "waitlist" | "social_direct_internal";

/** Canonical roles, in display order (admin first). Drives the Admin Panel checkboxes. */
export const ALL_ROLES: Role[] =
  ["admin", "creator_management", "social_boosting", "invoicing", "partner_network", "waitlist",
   "social_direct_internal"];

export const ROLE_LABELS: Record<Role, string> = {
  admin: "Admin",
  creator_management: "Creator Management",
  social_boosting: "Social Boosting",
  invoicing: "Invoicing",
  partner_network: "Partner Network",
  // TEMPORARY (SOC-604) — gates only the Creator CRM's Applied tab, not the whole CRM (which
  // stays on creator_management, same as always). See canViewAppliedTab below.
  waitlist: "Waitlist",
  // The Social Brand internal report: margin, media cost and gross profit per client. Granted per
  // person, never implied by another role (admin aside).
  social_direct_internal: "Social Brand (Internal)",
};

/** The headings the Admin Panel groups roles under, in display order. Purely presentational — a
 *  group grants nothing; it exists so a long role list reads as a few short sections. */
export type RoleGroup = "platform" | "social" | "creators" | "finance" | "partners";

export const ROLE_GROUPS: { id: RoleGroup; label: string }[] = [
  { id: "platform", label: "Platform" },
  { id: "social",   label: "Social" },
  { id: "creators", label: "Creators" },
  { id: "finance",  label: "Finance" },
  { id: "partners", label: "Partners" },
];

/** What the Admin Panel says about each role. `description` must stay true to ROLE_VIEWS /
 *  rolesCan below — it is the operator's only explanation of what ticking the box does. */
export const ROLE_META: Record<Role, { group: RoleGroup; description: string }> = {
  admin:              { group: "platform", description: "Every page, plus managing users and roles." },
  social_boosting:    { group: "social",   description: "Recipes, Launcher, Launcher Tracker, Ad Copy Review, Performance (read + write) and Client Relations, including its Launch activity list." },
  creator_management: { group: "creators", description: "Content, Creator Management, Client Relations and Performance (read-only)." },
  waitlist:           { group: "creators", description: "The Applied tab inside Client Relations. Needs Creator Management to reach the page." },
  invoicing:          { group: "finance",  description: "Invoices." },
  partner_network:    { group: "partners", description: "Partner Network." },
  social_direct_internal: { group: "social", description: "The Social Brand internal report: margin, media cost and gross profit per client. Granted per person." },
};

/** Display names for the pages ROLE_VIEWS can grant. Only pages a user can actually reach from the
 *  Sidebar are listed: analytics/runs are route-gated but have no nav item, so naming them in
 *  "effective access" would advertise pages nobody can find. */
export const PAGE_LABELS: Record<string, string> = {
  dashboard: "Recipes",
  content: "Content",
  launcher: "Launcher",
  launcher_tracker: "Launcher Tracker",
  ad_copy_review: "Ad Copy Review",
  performance: "Performance",
  rules: "Auto Rules",
  creators_admin: "Creator Management",
  partner_network: "Partner Network",
  creator_crm: "Client Relations",
  invoices: "Invoices",
};

/** Always-admin, hardcoded. Lowercase emails. Keep in sync with firestore.rules. */
export const BOOTSTRAP_ADMINS = [
  "darren.yip@ad.net",
  "peter.oropeza@ad.net",
  "benson.hon@ad.net",
];

/** Capabilities gate UI ACTIONS (as opposed to page visibility). Add more as roles grow. */
export type Capability = "manage_users" | "performance_write";

export const CAPABILITY_LABELS: Record<Capability, string> = {
  manage_users: "Manage users",
  performance_write: "Edit Performance",
};

/** The pages (Sidebar `view` literals) each NON-admin role grants. admin gets all pages (see
 *  canViewWithRoles). Recipe sub-routes (/templates/*) gate as "dashboard". analytics/runs have no
 *  nav item but their routes are gated here too so a locked-out user can't reach them by URL.
 *  creator_management gets "performance" too, but read-only — see the "performance_write"
 *  capability below, which it does NOT hold. creator_crm (Lex's creator-success team) is folded
 *  into creator_management rather than kept as its own role; social_boosting gets it too, for the
 *  Launch activity list inside it (canViewLaunchActivity).
 *
 *  "waitlist" grants no page of its own (empty list below) — it ONLY unlocks the Applied tab
 *  WITHIN creator_crm (still gated at the page level by creator_management/social_boosting above); see
 *  canViewAppliedTab below. ROLE_VIEWS is page-level, and the Applied tab is a sub-view of
 *  one page, not a page in Sidebar's terms. */
export const ROLE_VIEWS: Record<Exclude<Role, "admin">, string[]> = {
  creator_management: ["content", "creators_admin", "performance", "creator_crm"],
  social_boosting: ["dashboard", "launcher", "launcher_tracker", "ad_copy_review", "performance", "analytics", "runs",
                    "creator_crm"],
  invoicing: ["invoices"],
  partner_network: ["partner_network"],
  waitlist: [],
  // Unlike every other entry, enforced server-side too: socialiq-bqv serves the report only to this
  // role or admin (socialiq_bqv/core/staff.py, whose tests read this line).
  social_direct_internal: ["internal_report"],
};

/** The Creator CRM's Applied tab is gated separately from the rest of the CRM (SOC-604,
 *  TEMPORARY): admin and "waitlist" only. Someone with creator_management but not "waitlist" sees
 *  every other CRM tab, just not this one. */
export function canViewAppliedTab(roles: Role[]): boolean {
  return roles.includes("admin") || roles.includes("waitlist");
}

/** Client Relations' Launch activity list (creator_launch_activity, the Monday.com Launch Activity
 *  board) is for the boosting team: admin and "social_boosting" only. social_boosting can open
 *  Client Relations itself (ROLE_VIEWS); creator_management can too, but without this list. */
export function canViewLaunchActivity(roles: Role[]): boolean {
  return roles.includes("admin") || roles.includes("social_boosting");
}

export function isBootstrapAdmin(email: string | null | undefined): boolean {
  return !!email && BOOTSTRAP_ADMINS.includes(email.toLowerCase());
}

/** Normalize a raw Firestore user doc to a Role[]. Accepts the new `roles` array and the legacy
 *  single `role` field (legacy "admin" → ["admin"]; legacy "viewer"/unknown → [] = locked out). */
export function parseRoles(data: Record<string, unknown> | undefined): Role[] {
  if (!data) return [];
  const raw = Array.isArray(data.roles)
    ? (data.roles as unknown[])
    : (data.role != null ? [data.role] : []);
  const valid = new Set<Role>(ALL_ROLES);
  return [...new Set(raw.map(String))].filter((r): r is Role => valid.has(r as Role));
}

/** The roles that actually apply: bootstrap admins are always admin; otherwise the stored roles. */
export function effectiveRoles(email: string | null | undefined, storedRoles: Role[]): Role[] {
  if (isBootstrapAdmin(email)) return ["admin"];
  return storedRoles;
}

export function isAdminRoles(roles: Role[]): boolean {
  return roles.includes("admin");
}

/** Can this set of roles see the given page (Sidebar `view`)? admin → every page. */
export function canViewWithRoles(roles: Role[], view: string): boolean {
  if (roles.includes("admin")) return true;
  return roles.some((r) => r !== "admin" && ROLE_VIEWS[r]?.includes(view));
}

export function rolesCan(roles: Role[], cap: Capability): boolean {
  if (roles.includes("admin")) return true;
  switch (cap) {
    case "manage_users":
      return false;
    case "performance_write":
      // Everyone who can view Performance may write to it except creator_management,
      // which gets read-only access.
      return roles.includes("social_boosting");
    default:
      return false;
  }
}

/** Everything a role set unlocks, for the Admin Panel's "effective access" preview. Built from the
 *  same functions the app gates with (canViewWithRoles / rolesCan / canViewAppliedTab /
 *  canViewLaunchActivity), so the
 *  preview cannot drift from what the user will actually see. */
export function effectiveAccess(roles: Role[]): { pages: string[]; capabilities: Capability[]; extras: string[] } {
  const pages = Object.keys(PAGE_LABELS).filter(v => canViewWithRoles(roles, v)).map(v => PAGE_LABELS[v]);
  const capabilities = (Object.keys(CAPABILITY_LABELS) as Capability[]).filter(c => rolesCan(roles, c));
  // The Applied tab is a sub-view of Client Relations, so it is only worth naming when the page
  // itself is reachable — waitlist alone grants a tab on a page the user can't open.
  const crm = canViewWithRoles(roles, "creator_crm");
  const extras = [
    ...(canViewAppliedTab(roles) && crm ? ["Client Relations › Applied tab"] : []),
    ...(canViewLaunchActivity(roles) && crm ? ["Client Relations › Launch activity"] : []),
  ];
  return { pages, capabilities, extras };
}

// ── Firestore user directory ──────────────────────────────────────────────────
//
// Keyed by EMAIL (lowercased), NOT the Firebase uid. A person can end up with several Auth uids for
// one email (an Auth user recreated during setup, or two sign-in methods); a uid-keyed directory
// then grows duplicate rows for the same person and roles attach to whichever uid — unreliable.
// Email is the stable identity here (domain-restricted Google accounts) and matches how the backend
// identifies callers, so one email → exactly one doc, and roles follow the person across uid churn.

/** Firestore doc id for a user = their lowercased email. Emails contain no '/', so they're valid ids. */
export function emailKey(email: string): string {
  return email.trim().toLowerCase();
}

export interface DirectoryUser {
  email: string;
  roles: Role[];
  lastLogin?: unknown;
  /** When an admin last changed this user's roles, and which admin (email) did it. */
  updatedAt?: unknown;
  updatedBy?: string;
}

/** Record the signed-in user in the directory (email + lastLogin). Never writes `roles`, so an
 *  existing assignment is preserved and a new user simply has none (→ locked out). */
export async function upsertUserOnLogin(email: string): Promise<void> {
  const key = emailKey(email);
  await setDoc(doc(db, "users", key), { email: key, lastLogin: serverTimestamp() }, { merge: true });
}

/** The stored roles for an email ([] if none / doc missing). */
export async function fetchStoredRoles(email: string): Promise<Role[]> {
  const snap = await getDoc(doc(db, "users", emailKey(email)));
  return parseRoles(snap.exists() ? (snap.data() as Record<string, unknown>) : undefined);
}

/** Everyone who has signed in (the directory), for the Admin Panel. */
export async function listDirectoryUsers(): Promise<DirectoryUser[]> {
  const snap = await getDocs(collection(db, "users"));
  return snap.docs.map(d => {
    const data = d.data() as Record<string, unknown>;
    return {
      email: String(data.email ?? d.id),
      roles: parseRoles(data),
      lastLogin: data.lastLogin,
      updatedAt: data.updatedAt,
      updatedBy: typeof data.updatedBy === "string" ? data.updatedBy : undefined,
    };
  }).sort((a, b) => a.email.localeCompare(b.email));
}

/** Admin action: set a user's full role set (Firestore rules restrict this to admins). Writes the
 *  new `roles` array and clears the legacy single `role` field so the two can't disagree.
 *  `updatedBy` records the acting admin so the panel can say who last changed someone's access. */
export async function setUserRoles(email: string, roles: Role[], updatedBy: string): Promise<void> {
  const deduped = [...new Set(roles)].filter((r): r is Role => ALL_ROLES.includes(r));
  const key = emailKey(email);
  await setDoc(doc(db, "users", key), {
    email: key, roles: deduped, role: null,
    updatedAt: serverTimestamp(), updatedBy: emailKey(updatedBy),
  }, { merge: true });
}
